/**
 * @fileoverview Change-password form.
 * @description Client-side validated (via React Hook Form + buildChangePasswordFormSchema) form:
 * current password, new password, and its confirmation (all three are sent — the API re-checks
 * the match server-side). Shows the same live password-policy checklist and confirm-password
 * match hint as SetPasswordForm (PasswordPolicyHints, driven by GET /identity/password-policy), and
 * the schema is rebuilt against that same policy so submit blocks exactly what the checklist shows
 * unmet. Shows the API's error message on failure, mirroring LoginForm's mutate/render pattern; on
 * success useChangePassword signs the user out and sends them to /login, since the backend revokes
 * every session. No heading of its own — rendered inside ChangePasswordModal (from Settings) and
 * ChangePasswordCard (the forced change after sign-in), which each supply their own title.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { type FormEvent } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  PasswordMatchHint,
  PasswordPolicyChecklist,
} from '@/features/auth/components/PasswordPolicyHints'
import { useChangePassword } from '@/features/auth/hooks/useChangePassword'
import { usePasswordPolicy } from '@/features/auth/hooks/usePasswordPolicy'
import {
  buildChangePasswordFormSchema,
  type ChangePasswordFormValues,
} from '@/features/auth/schemas/change-password-form.schema'
import Button from '@/shared/components/atoms/Button'
import PasswordField from '@/shared/components/molecules/PasswordField'

interface ChangePasswordFormProps {
  testIdPrefix?: string
}

interface ChangePasswordFooterProps {
  errorMessage: string | undefined
  isPending: boolean
  testIdPrefix: string
}

/**
 * @component
 * @param {ChangePasswordFooterProps} props - The submit state.
 * @param {string | undefined} props.errorMessage - The failed change's message, when there is one.
 * @param {boolean} props.isPending - Whether the change request is in flight.
 * @param {string} props.testIdPrefix - Prefix of the QA locators.
 */
function ChangePasswordFooter({
  errorMessage,
  isPending,
  testIdPrefix,
}: ChangePasswordFooterProps) {
  const { t } = useTranslation()

  return (
    <>
      {errorMessage !== undefined && (
        <p role="alert" className="text-[14px] text-red-600" data-testid={`${testIdPrefix}-error`}>
          {errorMessage}
        </p>
      )}

      <Button type="submit" isLoading={isPending} data-testid={`${testIdPrefix}-submit-button`}>
        {isPending
          ? t('settings.changePassword.submitPending')
          : t('settings.changePassword.submitIdle')}
      </Button>
    </>
  )
}

/**
 * @component
 * @param {ChangePasswordFormProps} props - Optional locator prefix.
 * @param {string} [props.testIdPrefix] - Prefix of the form's QA locators (default
 * `change-password`): `<prefix>-form`, `-current-input`, `-new-input`, `-confirm-input`,
 * `-policy`, `-match`, `-error` and `-submit-button`.
 */
function ChangePasswordForm({ testIdPrefix = 'change-password' }: ChangePasswordFormProps) {
  const { t } = useTranslation()
  const changePasswordMutation = useChangePassword()
  const { data: policy } = usePasswordPolicy()
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(buildChangePasswordFormSchema(policy)),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  const newPassword = useWatch({ control, name: 'newPassword' })
  const confirmPassword = useWatch({ control, name: 'confirmPassword' })

  /**
   * @description Submits the change-password mutation, clearing the form on success.
   * @param {ChangePasswordFormValues} values - The validated current/new/confirm passwords.
   */
  const onValid = (values: ChangePasswordFormValues) => {
    changePasswordMutation.mutate(values, { onSuccess: () => reset() })
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid. Wrapped so the DOM
   * onSubmit handler returns void rather than the Promise React Hook Form's handleSubmit produces.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      className="flex flex-col gap-[22px]"
      onSubmit={onSubmit}
      noValidate
      data-testid={`${testIdPrefix}-form`}
    >
      <PasswordField
        id="currentPassword"
        data-testid={`${testIdPrefix}-current-input`}
        label={t('settings.changePassword.currentPasswordLabel')}
        autoComplete="current-password"
        error={errors.currentPassword && t(errors.currentPassword.message ?? '')}
        {...register('currentPassword', { onChange: () => changePasswordMutation.reset() })}
      />

      <div>
        <PasswordField
          id="newPassword"
          data-testid={`${testIdPrefix}-new-input`}
          label={t('settings.changePassword.newPasswordLabel')}
          autoComplete="new-password"
          error={errors.newPassword && t(errors.newPassword.message ?? '')}
          {...register('newPassword', { onChange: () => changePasswordMutation.reset() })}
        />
        <PasswordPolicyChecklist
          policy={policy}
          value={newPassword}
          data-testid={`${testIdPrefix}-policy`}
        />
      </div>

      <div>
        <PasswordField
          id="confirmPassword"
          data-testid={`${testIdPrefix}-confirm-input`}
          label={t('settings.changePassword.confirmPasswordLabel')}
          autoComplete="new-password"
          error={errors.confirmPassword && t(errors.confirmPassword.message ?? '')}
          {...register('confirmPassword', { onChange: () => changePasswordMutation.reset() })}
        />
        <PasswordMatchHint
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          data-testid={`${testIdPrefix}-match`}
        />
      </div>

      <ChangePasswordFooter
        errorMessage={
          changePasswordMutation.isError ? changePasswordMutation.error.message : undefined
        }
        isPending={changePasswordMutation.isPending}
        testIdPrefix={testIdPrefix}
      />
    </form>
  )
}

export default ChangePasswordForm
